import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getHoursAudit } from '../../api/timesheetApi';
import { usePermissions } from '../PermissionsContext';
import {
  MONTH_NAMES, DOW_FULL,
  addDays, firstOfMonth, lastOfMonth, fmtIso, mondayOf,
  parseIso, fmtDdMm, fmtDdMmYy,
} from '../Reports/calendarUtils';
import './HoursAuditLogPage.css';

// HLOG-S1 — admin Hours Audit Log.
// Reference: docs/presentations/hybrid-pm-concept/hours-audit-log.html.
// The chunk is by CHANGED-AT date (not work date); m/w/d navigation
// mirrors the timesheet convention (Month|Week|Day segmented, ◀▶,
// Today). The 8 columns and Excel-style funnels on every column
// except Previous / New value are 1:1 with the approved mockup.

// The mockup week is Mon..Sun; calendarUtils.rangeOf('w') is Mon..Fri
// (used by team-calendar reports). Local helper for HLOG.
const weekRange = (anchor) => {
  const mon = mondayOf(anchor);
  return { from: mon, to: addDays(mon, 6) };
};

const rangeOf = (view, anchor) => {
  if (view === 'm') return { from: firstOfMonth(anchor), to: lastOfMonth(anchor) };
  if (view === 'w') return weekRange(anchor);
  return { from: anchor, to: anchor };
};

const navStep = (view, anchor, dir) => {
  if (view === 'm') return new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
  if (view === 'w') return addDays(anchor, 7 * dir);
  return addDays(anchor, dir);
};

const periodLabel = (view, anchor) => {
  if (view === 'm') return `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getFullYear()}`;
  if (view === 'w') {
    const { from, to } = weekRange(anchor);
    return `${fmtDdMm(from)} – ${fmtDdMmYy(to)}`;
  }
  return `${DOW_FULL[anchor.getDay()]}, ${fmtDdMmYy(anchor)}`;
};

const fmtHoursValue = (v) => {
  if (v == null) return '—';
  const n = Number(v) || 0;
  return (Math.round(n * 10) / 10).toString().replace('.', ',');
};

const fmtChangedAt = (isoZ) => {
  if (!isoZ) return '';
  const d = new Date(isoZ);
  if (isNaN(d.getTime())) return isoZ;
  // Show local time; sv-SE emits yyyy-mm-dd HH:MM.
  return d.toLocaleString('sv-SE').slice(0, 16);
};

const FILTER_KEYS = ['chg', 'actor', 'proj', 'wi', 'work', 'op'];

export default function HoursAuditLogPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const [view, setView] = useState('m');
  const [anchor, setAnchor] = useState(() => new Date());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, []])));
  const [openFunnel, setOpenFunnel] = useState(null);
  const [funnelSearch, setFunnelSearch] = useState('');

  const range = useMemo(() => rangeOf(view, anchor), [view, anchor]);

  const load = useCallback(async () => {
    const havePrev = rows != null;
    if (havePrev) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const data = await getHoursAudit(fmtIso(range.from), fmtIso(range.to));
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load audit log.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Any click outside a funnel closes it.
    const onDocClick = () => { setOpenFunnel(null); setFunnelSearch(''); };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  if (permLoading) return <div className="hlog-page">Loading permissions…</div>;
  if (!hasPermission || !hasPermission('Timesheet.ViewHoursAudit')) {
    return <div className="hlog-page">You don't have access to the Hours Audit Log.</div>;
  }

  const goToday = () => setAnchor(new Date());
  const nav = (dir) => setAnchor((a) => navStep(view, a, dir));
  const switchView = (v) => { if (v !== view) setView(v); };

  const rawRows = rows || [];
  // Each key maps to the FIELD OF THE ROW that its filter compares.
  const passes = (row) => {
    const changedDate = (row.changedAt || '').slice(0, 10);
    const wiLabel = `WI-${row.workItemId} ${row.workItemTitle}`;
    const perColumn = {
      chg: changedDate,
      actor: row.changedByName,
      proj: row.projectName,
      wi: wiLabel,
      work: row.workDate,
      op: row.operation,
    };
    for (const k of FILTER_KEYS) {
      const selected = filters[k];
      if (selected.length === 0) continue; // empty = all
      if (!selected.includes(perColumn[k])) return false;
    }
    return true;
  };

  const filtered = rawRows.filter(passes);

  const uniqueValues = (getter) => {
    const set = new Set(rawRows.map(getter).filter((v) => v != null && v !== ''));
    return Array.from(set).sort();
  };
  const values = {
    chg: uniqueValues((r) => (r.changedAt || '').slice(0, 10)).reverse(),
    actor: uniqueValues((r) => r.changedByName),
    proj: uniqueValues((r) => r.projectName),
    wi: uniqueValues((r) => `WI-${r.workItemId} ${r.workItemTitle}`),
    work: uniqueValues((r) => r.workDate).reverse(),
    op: uniqueValues((r) => r.operation),
  };

  const toggleFP = (k, ev) => {
    ev.stopPropagation();
    setOpenFunnel((cur) => (cur === k ? null : k));
    setFunnelSearch('');
  };
  const toggleFV = (k, v, ev) => {
    ev.stopPropagation();
    setFilters((prev) => {
      const cur = prev[k] || [];
      const has = cur.includes(v);
      return { ...prev, [k]: has ? cur.filter((x) => x !== v) : [...cur, v] };
    });
  };
  const clearF = (k, ev) => {
    ev.stopPropagation();
    setFilters((prev) => ({ ...prev, [k]: [] }));
  };

  const funnel = (k, vals) => {
    const active = filters[k].length > 0;
    return (
      <>
        <span
          className={`hlog-fun${active ? ' hlog-fun-act' : ''}`}
          data-testid={`hlog-fun-${k}`}
          onClick={(ev) => toggleFP(k, ev)}
        >▼</span>
        {openFunnel === k && (
          <div className="hlog-fpanel" onClick={(ev) => ev.stopPropagation()}>
            <input
              type="text"
              placeholder="search…"
              data-testid={`hlog-fun-search-${k}`}
              value={funnelSearch}
              onChange={(ev) => setFunnelSearch(ev.target.value)}
            />
            <div className="hlog-fvals">
              {vals
                .filter((v) => v.toLowerCase().includes(funnelSearch.toLowerCase()))
                .map((v) => (
                  <label key={v} data-testid={`hlog-fun-opt-${k}-${v}`}>
                    <input
                      type="checkbox"
                      checked={filters[k].includes(v)}
                      onChange={(ev) => toggleFV(k, v, ev)}
                    /> {v}
                  </label>
                ))}
            </div>
            <span className="hlog-clr" data-testid={`hlog-fun-clear-${k}`} onClick={(ev) => clearF(k, ev)}>
              Clear filter
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="hlog-page">
      <div className="hlog-pageh"><h1>Hours Audit Log</h1></div>
      <div className="hlog-panel">
        <div className="hlog-ph">
          <span className="hlog-arr" data-testid="hlog-prev" onClick={() => nav(-1)}>◀</span>
          <span className="hlog-plabel" data-testid="hlog-period-label">{periodLabel(view, anchor)}</span>
          <span className="hlog-arr" data-testid="hlog-next" onClick={() => nav(1)}>▶</span>
          <span className="hlog-today" data-testid="hlog-today" onClick={goToday}>Today</span>
          <div className="hlog-seg" role="tablist">
            <div className={view === 'm' ? 'on' : ''} data-testid="hlog-view-m" onClick={() => switchView('m')}>Month</div>
            <div className={view === 'w' ? 'on' : ''} data-testid="hlog-view-w" onClick={() => switchView('w')}>Week</div>
            <div className={view === 'd' ? 'on' : ''} data-testid="hlog-view-d" onClick={() => switchView('d')}>Day</div>
          </div>
          <span className="hlog-cnt" data-testid="hlog-count">{filtered.length} changes</span>
        </div>
        <div className="hlog-pb">
          {error && <div className="hlog-error" data-testid="hlog-error">{error}</div>}
          {loading ? (
            <div className="hlog-empty" data-testid="hlog-loading">Loading…</div>
          ) : (
            <div className={`hlog-body${refreshing ? ' hlog-refreshing' : ''}`} data-testid="hlog-body">
              <table className="hlog-g" data-testid="hlog-table">
                <thead>
                  <tr>
                    <th>Changed at{funnel('chg', values.chg)}</th>
                    <th>Changed by{funnel('actor', values.actor)}</th>
                    <th>Project{funnel('proj', values.proj)}</th>
                    <th>Task / activity{funnel('wi', values.wi)}</th>
                    <th>Work date{funnel('work', values.work)}</th>
                    <th>Previous value</th>
                    <th>New value</th>
                    <th>Notes{funnel('op', values.op)}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="hlog-empty-row" data-testid="hlog-empty-row">
                        No changes in this period (or the filter narrowed everything out).
                      </td>
                    </tr>
                  )}
                  {filtered.map((r) => (
                    <tr key={r.id} data-testid={`hlog-row-${r.id}`}>
                      <td className="hlog-ts">{fmtChangedAt(r.changedAt)}</td>
                      <td>{r.changedByName}</td>
                      <td>{r.projectName}</td>
                      <td>
                        <span className={`hlog-chip ${r.isSystemWi ? 'hlog-chip-nb' : 'hlog-chip-wi'}`}>
                          {r.isSystemWi ? 'NB' : 'WI'}
                        </span>{' '}
                        WI-{r.workItemId} {r.workItemTitle}
                      </td>
                      <td className="hlog-ts">{fmtDdMmYy(parseIso(r.workDate))}</td>
                      <td className="hlog-n">{fmtHoursValue(r.oldHours)}</td>
                      <td className="hlog-n">{fmtHoursValue(r.newHours)}</td>
                      <td>{r.operation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
